import { createEmitter } from 'src/lib/Emitter';
import i18n from 'src/lib/i18n/i18n';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { MinigameButton } from './MinigameButton';
import {
  canPlayLeague,
  getCurrentLeaguePhase,
} from 'src/replicant/match2/leagues/leagues.getters';
import { getMatch2MaxLevel } from 'src/replicant/match2/match2.getters';
import { user } from 'src/StateObserver';
import { getRootView } from 'src/lib/utils';
import { getRandomFloat, getRandomItemFromArray } from '@play-co/components';
import {
  ImageScaleView,
  ImageScaleViewOpts,
  QuickViewPool,
  getRelativeOffset,
} from '@play-co/timestep-core/ui';
import { isDynamicTestInBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { SpineView, spineLoader } from '@play-co/timestep-spine';
import gsap from 'gsap';
import sounds from 'src/lib/sounds';
import { rescueLevelGetActive } from 'src/replicant/rescueLevel/rescueLevel.getters';

// Path for the spine animation, used in the level up animation
const spinePath = 'assets/ui/shared/animation/playButton/button-overlay';
export class Match2MinigameButton extends MinigameButton {
  private spine?: SpineView;
  private particlePool?: QuickViewPool<ImageScaleView>;
  private levelText: LangBitmapFontTextShadowView;

  constructor() {
    super();

    // Update the button to match the design.
    this.setSize(139, 157);

    // "replace" the label with a shadowed one
    this.levelText = new LangBitmapFontTextShadowView({
      superview: this,
      color: 'white',
      localeText: () => i18n('footer.slots'),
      width: this.label.style.width,
      align: 'center',
      x: 8,
      y: 94,
      size: 34,
      font: 'Body',
      textShadowOffsetX: 0,
      textShadowColor: 'black',
    });

    this.label.hide();
    this.icon?.removeFromSuperview();

    // Update the anchor point to center-bottom
    this.updateOpts({
      anchorX: this.style.width / 2,
      anchorY: this.style.height,
    });
    // Run a first update to set the initial level
    this.updateLevel();
  }

  public setActive(_: boolean) {
    // no-op
  }

  private updateLevel(args?: {
    level: number;
    playLeague: boolean;
    leaguePhase: number;
  }) {
    if (rescueLevelGetActive(user())) {
      this.updateButtonImage('assets/ui/shared/buttons/btn_footer_rescue.png');
      this.levelText.localeText = () => i18n('rescueLevel.common.button');
      this.levelText.updateOpts({ offsetY: 10 });
      return;
    }

    // Reset any offset applied for rescue level
    this.levelText.updateOpts({ offsetY: 0 });

    const { level, playLeague, leaguePhase } = args ?? {
      level: user().match2.level,
      playLeague: canPlayLeague(user()),
      leaguePhase: getCurrentLeaguePhase(user()),
    };

    if (playLeague) {
      this.updateButtonImage('assets/ui/match2/leagues/play_button_old.png');
      this.levelText.localeText = () => `Ph.${leaguePhase}`;
      return;
    }

    this.updateButtonImage('assets/ui/shared/buttons/btn_footer_match2.png');
    this.levelText.localeText = () =>
      level <= getMatch2MaxLevel(user()) ? `Lv.${level}` : `MAX`;
  }

  public async playLevelUp() {
    // Start loading spine in advance
    void spineLoader(spinePath);

    await gsap.to(this.style, {
      scaleX: 1.25,
      scaleY: 0.6,
      duration: 0.4,
      ease: 'sine.inOut',
    });
    await gsap.to(this.style, {
      scaleX: 0.92,
      scaleY: 1.08,
      duration: 0.15,
      ease: 'sine.out',
    });

    this.updateLevel();
    this.playSpineOverlay();
    this.playParticles();
    sounds.playSound('puzzle-lock-destroy', 0.5);

    await gsap.to(this.style, {
      scaleX: 1.05,
      scaleY: 0.93,
      duration: 0.15,
      ease: 'sine.out',
    });
    await gsap.to(this.style, {
      scaleX: 1,
      scaleY: 1,
      duration: 0.15,
      ease: 'sine.out',
    });
  }

  private async playSpineOverlay() {
    if (!this.spine) {
      await spineLoader(spinePath);
      this.spine = new SpineView({
        superview: this,
        spinePath,
        zIndex: 1,
        scaleX: 0.9,
        scaleY: 0.97,
        x: 70,
        y: 157,
        canHandleEvents: false,
      });
    }

    // Play the animation
    this.spine.updateOpts({ opacity: 1 });
    this.spine.state.setAnimation(0, 'overlay', false);

    // Fade out th overlay so it doesn't disappear abruptly
    gsap.to(this.spine.style, {
      opacity: 0,
      duration: 0.3,
      delay: 0.2,
      ease: 'linear',
    });
  }

  private playParticles() {
    if (!this.particlePool) {
      this.particlePool = new QuickViewPool(() => {
        const particle = new ImageScaleView({
          zIndex: 2,
          image: 'assets/ui/shared/particles/particle-star.png',
          centerOnOrigin: true,
          scaleMethod: 'contain',
          canHandleEvents: false,
        });
        return particle;
      });
    }

    const particleImages: ImageScaleViewOpts[] = [
      {
        image: 'assets/ui/shared/particles/particle-dot.png',
        width: 25,
        height: 25,
      },
      {
        image: 'assets/ui/shared/particles/particle-star.png',
        width: 35,
        height: 35,
      },
    ];

    // Particles are added to root view so they don't get affected by the bounce animation
    const superview = getRootView();
    const position = getRelativeOffset(this, superview);
    const numParticles = 50;

    for (let i = 0; i < numParticles; i++) {
      const particle = this.particlePool.obtainView();
      if (!particle) continue;

      // Reset the particle
      particle.updateOpts({
        superview,
        ...getRandomItemFromArray(particleImages, Math.random),
        x: position.x + Math.random() * this.style.width,
        y: position.y + Math.random() * this.style.height - 10,
        scale: getRandomFloat(0.7, 1.3, Math.random),
        zIndex: 999,
        opacity: 1,
      });

      // Find particle angle relative to the button center
      const angle = Math.atan2(
        particle.style.x - (position.x + this.style.width / 2),
        particle.style.y - (position.y + this.style.height),
      );

      // Calculate distance for particle to travel
      const distance = getRandomFloat(100, 300, Math.random);

      // Move particle away from the center, consideing the angle and distance
      const toX = particle.style.x + Math.sin(angle) * distance;
      const toY = particle.style.y + Math.cos(angle) * distance;
      const duration = getRandomFloat(0.5, 0.9, Math.random);

      // Animate the particle to target position
      gsap.to(particle.style, {
        x: toX,
        y: toY,
        duration,
        ease: 'sine.out',
      });

      // Animate the particle after a delay
      gsap
        .to(particle.style, {
          duration: duration / 2,
          ease: 'sine.in',
          opacity: 0,
          delay: duration / 2,
        })
        .then(() => {
          particle.removeFromSuperview();
          this.particlePool?.releaseView(particle);
        });
    }
  }
}
